// src/components/business/BusinessSocialTab.tsx
//
// DEM-2118 (Marketing F4b) — sub-aba "Redes Sociais" da aba Marketing do business.
//
// 100% via conector, SEM modo URL/handle (decisão v1.1 do Breno: não existe etapa
// de digitar URL). A sub-aba é uma PROJEÇÃO PURA de connector_connections filtrada
// por business_id (da aba) + category=social_media — zero tabela própria, zero
// estado duplicado (AC#1).
//
// Fluxo "Adicionar rede social" (AC#2): botão → grid com as redes do catálogo
// social_media → escolher a rede → ConnectModal (reusado do módulo compartilhado
// dos conectores: OAuth2 / api_key / MCP conforme a matriz). A conexão criada aqui
// nasce com business_id preenchido (presetBusinessId) e aparece também em
// Configurações › Conectores marcada com o business (AC#3).
//
// Card da conta (AC#4): avatar, handle e URL do perfil vindos do external_account
// + status da conexão. Ações reconectar (oauth/authorize?reconnect_connection_id=)
// e revogar (DELETE :id) (AC#5). Múltiplas contas da mesma rede = cards distintos
// (1 connection = 1 card) (AC#6). Nenhum input de URL/handle manual (AC#7).

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type Business,
  type ConnectorCatalogItem,
  type ConnectorConnection,
} from '@/lib/api';
import { ConnectModal } from '@/components/connectors/shared';
import {
  readExternalAccount,
  canConnectConnector,
  CATALOG_STATUS,
  CONN_STATUS,
  MODE_LABELS,
} from '@/components/connectors/connectors-ui';
import { WfEmpty } from '@/components/wf';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import {
  Share2,
  Plus,
  Plug,
  Loader2,
  Trash2,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Blocks,
  X,
} from 'lucide-react';

interface BusinessSocialTabProps {
  businessId: string;
}

/**
 * Sub-aba Redes Sociais. Carrega em paralelo:
 *   - catálogo (filtrado client-side por category=social_media pro grid);
 *   - conexões do business (projeção: listConnectorConnections({ business_id })).
 * As conexões já vêm sem as revogadas (backend exclui por default).
 */
export function BusinessSocialTab({ businessId }: BusinessSocialTabProps) {
  const { toast } = useToast();
  const [catalog, setCatalog] = useState<ConnectorCatalogItem[]>([]);
  const [connections, setConnections] = useState<ConnectorConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // DEM-2128: redirect URI do callback central do Core (por instalação) — modal BYO.
  const [redirectUri, setRedirectUri] = useState<string>('');

  // grid "adicionar rede social" (aberto/fechado)
  const [showGrid, setShowGrid] = useState(false);
  // conector escolhido no grid → abre o ConnectModal
  const [connectFor, setConnectFor] = useState<ConnectorCatalogItem | null>(null);
  // reconexão de uma conexão específica (AC#5)
  const [reconnectFor, setReconnectFor] = useState<ConnectorConnection | null>(null);
  // confirmação + estado de revogação (AC#5)
  const [confirmRevoke, setConfirmRevoke] = useState<ConnectorConnection | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cat, conns] = await Promise.all([
        api.getConnectorCatalog(),
        // AC#1: projeção por business_id (o backend filtra pelo vínculo).
        api.listConnectorConnections({ business_id: businessId }),
      ]);
      setCatalog(cat.connectors);
      setRedirectUri(cat.redirect_uri);
      setConnections(conns);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar redes sociais.');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Só as redes sociais do catálogo (AC#2 — grid filtrado por category=social_media).
  const socialCatalog = useMemo(
    () => catalog.filter((c) => c.category === 'social_media'),
    [catalog],
  );

  // Índice slug → catálogo, pra resolver metadados (nome/logo/modos) do card e do grid.
  const catalogBySlug = useMemo(() => {
    const m = new Map<string, ConnectorCatalogItem>();
    for (const c of catalog) m.set(c.slug, c);
    return m;
  }, [catalog]);

  // AC#1: SOMENTE conexões cujo conector é category=social_media. A projeção por
  // business_id já vem do backend; aqui filtramos por categoria (defensivo) e
  // descartamos revogadas (que também não devem voltar por default).
  const socialConnections = useMemo(() => {
    const socialSlugs = new Set(socialCatalog.map((c) => c.slug));
    return connections.filter(
      (conn) => conn.status !== 'revoked' && socialSlugs.has(conn.connector_slug),
    );
  }, [connections, socialCatalog]);

  const businessesForModal: Business[] = useMemo(() => [], []);

  async function handleRevoke(conn: ConnectorConnection) {
    setRevokingId(conn.id);
    setConfirmRevoke(null);
    try {
      await api.revokeConnectorConnection(conn.id);
      toast({ message: `Conexão "${conn.display_name}" revogada.`, type: 'success' });
      await load();
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'Erro ao revogar conexão.',
        type: 'error',
      });
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div className="wf-bizpanel" data-testid="business-social-tab">
      {/* Cabeçalho + CTA "Adicionar rede social" (AC#2) */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-raios-text inline-flex items-center gap-2">
            <Share2 className="h-4 w-4 text-raios-muted" aria-hidden /> Redes Sociais
          </h3>
          <p className="text-xs text-raios-muted mt-0.5">
            Contas conectadas via conector — projeção direta das conexões deste negócio. Sem
            digitar URL: tudo vem do provedor.
          </p>
        </div>
        <Button variant="brand" size="sm" onClick={() => setShowGrid(true)}>
          <Plus className="h-3.5 w-3.5" /> Adicionar rede social
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-raios-muted py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Carregando redes sociais…
        </div>
      ) : error ? (
        <Card className="p-6">
          <div className="flex items-start gap-2 text-sm text-raios-danger-text">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-px" aria-hidden />
            <span>{error}</span>
          </div>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => void load()}>
            <RefreshCw className="h-3.5 w-3.5" /> Tentar de novo
          </Button>
        </Card>
      ) : socialConnections.length === 0 ? (
        <WfEmpty icon={<Share2 />}>
          Nenhuma rede social conectada ainda. Use “Adicionar rede social” para conectar um perfil
          — o handle, avatar e URL vêm do provedor automaticamente.
        </WfEmpty>
      ) : (
        <div className="flex flex-col gap-2">
          {socialConnections.map((conn) => {
            const connector = catalogBySlug.get(conn.connector_slug);
            const cs = CONN_STATUS[conn.status] ?? CONN_STATUS.connected;
            const ext = readExternalAccount(conn.external_account);
            const isExpired = conn.status === 'expired';
            // Reconexão OAuth só faz sentido quando a rede é OAuth (AC#5).
            const supportsOauth = connector?.supported_modes.includes('oauth2') ?? false;
            const canReconnectOauth = supportsOauth && conn.auth_mode === 'oauth2';
            const canConnect = connector ? canConnectConnector(connector) : false;
            return (
              <Card key={conn.id} className="p-4" data-testid={`social-card-${conn.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Avatar do provider (external_account.avatar — AC#4). */}
                  {ext.avatar ? (
                    <img
                      src={ext.avatar}
                      alt=""
                      aria-hidden
                      className="h-9 w-9 rounded-full object-cover border border-raios-border shrink-0"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-raios-elevated/40 border border-raios-border flex items-center justify-center shrink-0">
                      {connector?.logo_url ? (
                        <img
                          src={connector.logo_url}
                          alt=""
                          aria-hidden
                          className="h-5 w-5 object-contain"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <Blocks className="h-4 w-4 text-raios-muted" aria-hidden />
                      )}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-raios-text truncate">
                        {connector?.name ?? conn.connector_slug}
                      </span>
                      <Badge variant={cs.variant} className="text-[10px]">
                        {cs.label}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        {MODE_LABELS[conn.auth_mode]}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-raios-muted flex-wrap">
                      <span className="truncate">{conn.display_name}</span>
                      {/* Handle + link do perfil (external_account — AC#4). */}
                      {ext.handle &&
                        (ext.profileUrl ? (
                          <a
                            href={ext.profileUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1 text-raios-accent-text hover:underline"
                          >
                            {ext.handle} <ExternalLink className="h-3 w-3" aria-hidden />
                          </a>
                        ) : (
                          <span>
                            conta <b className="text-raios-text-secondary">{ext.handle}</b>
                          </span>
                        ))}
                      {conn.last_verified_at && (
                        <span>
                          verificada{' '}
                          <b className="text-raios-text-secondary">
                            {new Date(conn.last_verified_at).toLocaleString('pt-BR')}
                          </b>
                        </span>
                      )}
                    </div>

                    {/* Status detail + aviso de reconexão quando expirado (AC#5). */}
                    {isExpired && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-raios-warning-text">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden />
                        <span>
                          Conexão expirada
                          {conn.status_detail ? ` (${conn.status_detail})` : ''}. Reconecte para
                          restabelecer o acesso.
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant={isExpired ? 'brand' : 'outline'}
                      size="sm"
                      onClick={() =>
                        canReconnectOauth && connector
                          ? setReconnectFor(conn)
                          : connector
                            ? setConnectFor(connector)
                            : undefined
                      }
                      disabled={!canConnect}
                      title="Reconectar (re-autenticar)"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Reconectar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-raios-danger-text"
                      onClick={() => setConfirmRevoke(conn)}
                      disabled={revokingId === conn.id}
                      title="Revogar"
                    >
                      {revokingId === conn.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                      Revogar
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ─── Grid "Adicionar rede social" (AC#2) ─────────────────────────────── */}
      {showGrid && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="social-grid-title"
        >
          <Card className="w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 id="social-grid-title" className="text-lg font-semibold text-raios-text">
                Adicionar rede social
              </h3>
              <button
                type="button"
                onClick={() => setShowGrid(false)}
                className="text-raios-muted hover:text-raios-text"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-raios-muted">
              Escolha a rede — você será levado ao fluxo de conexão do conector correspondente
              (OAuth2, API Key ou MCP, conforme a rede).
            </p>

            {socialCatalog.length === 0 ? (
              <p className="text-sm text-raios-muted py-6 text-center">
                Nenhuma rede social disponível no catálogo.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {socialCatalog.map((item) => {
                  const st = CATALOG_STATUS[item.status] ?? CATALOG_STATUS.available;
                  const connectable = canConnectConnector(item);
                  return (
                    <button
                      key={item.slug}
                      type="button"
                      disabled={!connectable}
                      onClick={() => {
                        setConnectFor(item);
                        setShowGrid(false);
                      }}
                      className={
                        'flex flex-col items-center gap-2 rounded-lg border p-3 text-center transition-colors ' +
                        (connectable
                          ? 'border-raios-border bg-raios-card hover:border-raios-accent/50 hover:bg-raios-accent/5 cursor-pointer'
                          : 'border-raios-border bg-raios-card/50 opacity-60 cursor-not-allowed')
                      }
                      title={
                        connectable
                          ? `Conectar ${item.name}`
                          : `${item.name} indisponível nesta fase`
                      }
                    >
                      <div className="h-10 w-10 rounded-lg bg-raios-elevated/40 border border-raios-border flex items-center justify-center overflow-hidden">
                        {item.logo_url ? (
                          <img
                            src={item.logo_url}
                            alt=""
                            aria-hidden
                            className="h-6 w-6 object-contain"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <Plug className="h-5 w-5 text-raios-muted" aria-hidden />
                        )}
                      </div>
                      <span className="text-xs font-medium text-raios-text truncate w-full">
                        {item.name}
                      </span>
                      {/* Status do catálogo (available/beta/coming_soon) — AC extra. */}
                      <Badge variant={st.variant} className="text-[9px]">
                        {st.label}
                      </Badge>
                      {/* Modos suportados — X aparece como MCP-only aqui. */}
                      <div className="flex flex-wrap gap-1 justify-center">
                        {item.supported_modes.map((m) => (
                          <Badge key={m} variant="outline" className="text-[9px]">
                            {MODE_LABELS[m]}
                          </Badge>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end pt-1">
              <Button variant="outline" onClick={() => setShowGrid(false)}>
                Fechar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ─── Modal de conexão (AC#2/AC#3) — vínculo ao business é automático ──── */}
      {connectFor && (
        <ConnectModal
          connector={connectFor}
          businesses={businessesForModal}
          presetBusinessId={businessId}
          redirectUri={redirectUri}
          onClose={() => setConnectFor(null)}
          onConnected={async () => {
            setConnectFor(null);
            await load();
          }}
        />
      )}

      {/* ─── Modal de RECONEXÃO OAuth de uma conexão específica (AC#5) ────────── */}
      {reconnectFor && catalogBySlug.get(reconnectFor.connector_slug) && (
        <ConnectModal
          connector={catalogBySlug.get(reconnectFor.connector_slug)!}
          businesses={businessesForModal}
          presetBusinessId={businessId}
          redirectUri={redirectUri}
          reconnectConnectionId={reconnectFor.id}
          onClose={() => setReconnectFor(null)}
          onConnected={async () => {
            setReconnectFor(null);
            await load();
          }}
        />
      )}

      {/* ─── Confirmação de revogação (AC#5) ─────────────────────────────────── */}
      {confirmRevoke && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-raios-text">Revogar conexão</h3>
            <p className="text-sm text-raios-text-secondary">
              Revogar <strong>{confirmRevoke.display_name}</strong>? A credencial é apagada do vault
              e o acesso é encerrado imediatamente.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
                Cancelar
              </Button>
              <Button
                variant="outline"
                className="text-raios-danger-text"
                onClick={() => void handleRevoke(confirmRevoke)}
              >
                <Trash2 className="h-3.5 w-3.5" /> Revogar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
